<template>
  <div class="news-center">
    <!-- 导航栏 -->
    <nav class="navbar">
      <div class="container">
        <div class="logo">
          <img src="@/assets/logo.png" alt="趣馋兔 Logo" class="logo-img">
          <h1>趣馋兔</h1>
        </div>
        <ul class="nav-menu">
          <li><a href="#/home" @click.prevent="navigateTo('home')">首页</a></li>
          <li><a href="#/home#products" @click.prevent="navigateTo('home', 'products')">产品中心</a></li>
          <li><a href="#/home#about" @click.prevent="navigateTo('home', 'about')">关于我们</a></li>
          <li><a href="#/home#news" @click.prevent="navigateTo('home', 'news')">新闻资讯</a></li>
          <li><a href="#/home#contact" @click.prevent="navigateTo('home', 'contact')">联系我们</a></li>
        </ul>
      </div>
    </nav>

    <!-- 新闻详情区域 -->
    <section v-if="selectedNewsDetail" class="news-detail-section">
      <div class="container">
        <button class="back-home-btn" @click="goBackToList">
          <span class="btn-icon">←</span>
          <span class="btn-text">返回列表</span>
        </button>
        <div class="detail-header">
          <h1 class="detail-title">{{ selectedNewsDetail.newsName }}</h1>
          <div class="detail-meta">
            <span class="detail-date">{{ formatDate(selectedNewsDetail.createTime) }}</span>
            <span class="detail-creator">发布人：{{ selectedNewsDetail.creator }}</span>
          </div>
        </div>
        <div class="detail-content" v-html="formatContent(selectedNewsDetail.newsContent)"></div>
      </div>
    </section>

    <!-- 新闻列表区域 -->
    <section v-else class="news-list-section">
      <div class="container">
        <div class="list-header">
          <!-- 返回首页按钮 -->
          <button class="back-home-btn" @click="goBackHome">
            <span class="btn-icon">←</span>
            <span class="btn-text">返回首页</span>
          </button>

          <div class="title-wrapper">
            <h2 class="page-title">新闻资讯</h2>
            <p class="page-subtitle">NEWS & EVENTS</p>
          </div>
        </div>

        <!-- 新闻列表 -->
        <div class="news-list-container">
          <div
            v-for="(newsItem, index) in newsList"
            :key="index"
            class="news-item"
            :class="{ active: selectedNewsId === newsItem.id }"
            @click="selectNews(newsItem.id)">
            <h3 class="news-title">{{ newsItem.newsName }}</h3>

            <div class="news-content-rich" v-html="truncateContent(newsItem.newsContent)"></div>
            <div class="read-more-btn" @click.stop="goToNewsDetail(newsItem.id)">
              资讯详情
            </div>
            <div class="news-footer">
              <span class="news-date">{{ formatDate(newsItem.createTime) }}</span>
              <span class="news-creator">作者：{{ newsItem.creator }}</span>
            </div>
          </div>
        </div>

        <!-- 空状态提示 -->
        <div v-if="newsList.length === 0 && !loading" class="empty-state">
          <p>暂无新闻资讯</p>
        </div>

        <!-- 加载中提示 -->
        <div v-if="loading" class="loading-state">
          <p>加载中...</p>
        </div>
      </div>
    </section>

    <!-- 页脚 -->
    <footer class="footer">
      <div class="container">
        <p>&copy; 2024 午申企业管理公司工作室 版权所有</p>
        <p>ICP 备案号：豫 ICP 备 2025119208 号 </p>
      </div>
    </footer>
  </div>
</template>

<script>
import { getNewsPage, getNewsDetail } from '@/api/news'

export default {
  name: 'NewsCenter',
  data() {
    return {
      newsList: [],
      loading: false,
      selectedNewsId: null,
      selectedNewsDetail: null,
      currentPage: 1,
      pageSize: 10,
      total: 0
    }
  },
  computed: {
    selectedNews() {
      return this.newsList.find(news => news.id === this.selectedNewsId)
    }
  },
  watch: {
    // 监听 URL 参数变化
    '$route.query.id': {
      handler(newVal) {
        console.log('监听到 URL 参数变化:', newVal)
        if (newVal && this.newsList.length > 0) {
          this.loadNewsDetail(Number(newVal))
        } else if (!newVal) {
          this.selectedNewsDetail = null
        }
      },
      immediate: true
    }
  },
  mounted() {
    console.log('NewsCenter mounted')
    console.log('当前 route:', this.$route)
    console.log('URL 参数 id:', this.$route.query.id)

    // 先加载列表数据
    this.loadNewsList().then(() => {
      // 列表加载完成后再检查是否需要加载详情
      if (this.$route.query.id) {
        console.log('列表加载完成，检测 URL 参数 id:', this.$route.query.id)
        this.loadNewsDetail(Number(this.$route.query.id))
      }
    })
  },
  methods: {
    // 加载新闻列表（分页）
    async loadNewsList() {
      this.loading = true
      try {
        const result = await getNewsPage(this.currentPage, this.pageSize)
        console.log('API 返回数据:', result)

        // 处理不同的返回数据结构
        if (Array.isArray(result)) {
          // 直接返回数组
          this.newsList = result
          console.log('新闻列表数据:', this.newsList)
        } else if (result && typeof result === 'object') {
          // 如果是对象，检查是否有 list 或 records 字段
          if (Array.isArray(result.list)) {
            this.newsList = result.list
            this.total = result.total || 0
            console.log('分页数据 (list):', this.newsList, '总数:', this.total)
          } else if (Array.isArray(result.records)) {
            this.newsList = result.records
            this.total = result.total || 0
            console.log('分页数据 (records):', this.newsList, '总数:', this.total)
          } else if (result.data && Array.isArray(result.data.list)) {
            // 嵌套在 data 字段中
            this.newsList = result.data.list
            this.total = result.data.total || 0
            console.log('分页数据 (data.list):', this.newsList, '总数:', this.total)
          } else {
            console.warn('未找到有效的数据数组:', result)
            this.newsList = []
          }
        } else {
          console.warn('API 返回的不是数组或对象:', result)
          this.newsList = []
        }

        console.log('最终 newsList:', this.newsList)
        console.log('newsList 长度:', this.newsList.length)
        return this.newsList
      } catch (error) {
        console.error('加载新闻列表失败:', error)
        this.newsList = []
        return []
      } finally {
        this.loading = false
      }
    },

    // 加载新闻详情
    async loadNewsDetail(newsId) {
      console.log('=== 开始加载新闻详情 ===')
      console.log('新闻 ID:', newsId)
      console.log('当前 newsList:', this.newsList)

      this.loading = true
      try {
        const result = await getNewsDetail(newsId)
        console.log('新闻详情 API 返回:', result)

        if (result) {
          this.selectedNewsDetail = result
          this.selectedNewsId = newsId
          console.log('详情数据已设置:', this.selectedNewsDetail)
        }
      } catch (error) {
        console.error('加载新闻详情失败:', error)
        // 如果 API 失败，尝试从列表中查找
        const news = this.newsList.find(n => n.id === newsId)
        if (news) {
          this.selectedNewsDetail = news
          this.selectedNewsId = newsId
          console.log('从本地列表找到新闻:', news)
        }
      } finally {
        this.loading = false
      }

      console.log('=== 加载新闻详情结束 ===')
    },

    // 格式化日期
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    // 截断内容
    truncateContent(content, maxLength = 150) {
      if (!content) return ''
      const text = content.replace(/<[^>]*>/g, '')
      if (text.length > maxLength) {
        return text.substring(0, maxLength) + '...'
      }
      return text
    },

    // 格式化内容（处理空内容的情况）
    formatContent(content) {
      if (!content) {
        return '<p style="color: #999; text-align: center; padding: 40px;">暂无详细内容</p>'
      }

      // 如果内容是纯文本（没有 HTML 标签），添加段落标签
      if (!content.includes('<')) {
        return `<p>${content}</p>`
      }

      return content
    },

    // 选择新闻
    selectNews(newsId) {
      this.selectedNewsId = newsId
    },

    // 查看新闻详情
    goToNewsDetail(newsId) {
      console.log('跳转到新闻详情，ID:', newsId)
      this.loadNewsDetail(newsId)
      this.$router.push({
        path: '/news',
        query: {
          id: newsId
        }
      })
    },

    // 返回列表
    goBackToList() {
      console.log('返回列表')
      this.selectedNewsDetail = null
      this.selectedNewsId = null
      this.$router.push({
        path: '/news'
      })
    },

    // 返回首页
    goBackHome() {
      console.log('返回首页')
      this.$router.push({
        name: 'HomePage'
      })
    },

    // 导航到指定页面和位置
    navigateTo(page, anchor = null) {
      console.log('导航到:', page, '锚点:', anchor)

      if (page === 'home') {
        this.$router.push({
          name: 'HomePage',
          hash: anchor ? `#${anchor}` : undefined
        }).then(() => {
          // 等待页面加载后滚动
          setTimeout(() => {
            if (anchor) {
              const element = document.getElementById(anchor)
              if (element) {
                const navHeight = 70 // 导航栏高度
                const elementPosition = element.getBoundingClientRect().top + window.pageYOffset
                window.scrollTo({
                  top: elementPosition - navHeight,
                  behavior: 'smooth'
                })
              }
            } else {
              window.scrollTo({
                top: 0,
                behavior: 'smooth'
              })
            }
          }, 100)
        })
      }
    }
  }
}
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.news-center {
  font-family: 'Microsoft YaHei', Arial, sans-serif;
  color: #333;
  line-height: 1.6;
  min-height: 100vh;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* 导航栏 */
.navbar {
  background: linear-gradient(135deg, #d32f2f 0%, #e53935 100%);
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
}

.navbar .container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
}

.logo {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-img {
  height: 50px;
  width: auto;
  object-fit: contain;
}

.logo h1 {
  color: #fff;
  font-size: 28px;
  font-weight: bold;
  margin: 0;
  line-height: 1;
}

.nav-menu {
  display: flex;
  list-style: none;
  gap: 40px;
}

.nav-menu a {
  text-decoration: none;
  color: #fff;
  font-size: 16px;
  transition: all 0.3s;
  font-weight: 500;
}

.nav-menu a:hover {
  color: #ffebee;
  transform: translateY(-2px);
}

/* 新闻详情区域 */
.news-detail-section {
  padding: 120px 0 80px;
  background: linear-gradient(180deg, #f9f9f9 0%, #fff 100%);
  min-height: calc(100vh - 70px);
}

.back-btn {
  background: linear-gradient(135deg, #d32f2f 0%, #e53935 100%);
  color: #fff;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 30px;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 2px 8px rgba(211, 47, 47, 0.2);
}

.back-btn:hover {
  background: linear-gradient(135deg, #e53935 0%, #ef5350 100%);
  transform: translateX(-3px);
  box-shadow: 0 4px 12px rgba(211, 47, 47, 0.3);
}

.detail-header {
  margin-bottom: 40px;
  padding-bottom: 30px;
  border-bottom: 3px solid #ffebee;
}

.detail-title {
  font-size: 32px;
  color: #d32f2f;
  margin-bottom: 20px;
  font-weight: bold;
  line-height: 1.5;
  text-align: left;
}

.detail-meta {
  display: flex;
  gap: 24px;
  color: #666;
  font-size: 14px;
  align-items: center;
}

.detail-date {
  color: #d32f2f;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
}

.detail-date::before {
  content: '📅';
  font-size: 16px;
}

.detail-creator {
  color: #666;
  font-style: normal;
  display: flex;
  align-items: center;
  gap: 6px;
}

.detail-creator::before {
  content: '✍️';
  font-size: 16px;
}

.detail-content {
  background: #fff;
  padding: 50px;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0,0,0,0.08);
  font-size: 16px;
  line-height: 2;
  color: #333;
  max-width: 100%;
  overflow-x: hidden;
}

.detail-content p {
  margin-bottom: 24px;
  text-align: justify;
  line-height: 2.2;
  letter-spacing: 0.5px;
}

.detail-content p:last-child {
  margin-bottom: 0;
}

/* 增强内容可读性 */
.detail-content h1,
.detail-content h2,
.detail-content h3,
.detail-content h4,
.detail-content h5,
.detail-content h6 {
  color: #d32f2f;
  margin: 30px 0 20px;
  font-weight: bold;
  line-height: 1.4;
}

.detail-content h1 {
  font-size: 28px;
  border-left: 4px solid #d32f2f;
  padding-left: 15px;
}

.detail-content h2 {
  font-size: 24px;
  border-left: 3px solid #e53935;
  padding-left: 12px;
}

.detail-content h3 {
  font-size: 20px;
  border-left: 2px solid #ef5350;
  padding-left: 10px;
}

.detail-content ul,
.detail-content ol {
  margin: 20px 0;
  padding-left: 30px;
}

.detail-content li {
  margin-bottom: 12px;
  line-height: 2;
}

.detail-content blockquote {
  margin: 30px 0;
  padding: 20px 30px;
  background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
  border-left: 4px solid #d32f2f;
  border-radius: 8px;
  color: #666;
  font-style: italic;
}

.detail-content img {
  max-width: 100%;
  height: auto;
  border-radius: 10px;
  margin: 25px 0;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  display: block;
  margin-left: auto;
  margin-right: auto;
}

.detail-content a {
  color: #d32f2f;
  text-decoration: none;
  border-bottom: 1px dashed #d32f2f;
  transition: all 0.3s;
}

.detail-content a:hover {
  color: #e53935;
  border-bottom-color: #e53935;
  background-color: rgba(211, 47, 47, 0.05);
}

/* 新闻列表区域 */
.news-list-section {
  padding: 120px 0 80px;
  background: linear-gradient(180deg, #f9f9f9 0%, #fff 100%);
  min-height: calc(100vh - 70px);
}

.list-header {
  margin-bottom: 50px;
}

.back-home-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  color: #666;
  border: 2px solid #e0e0e0;
  padding: 10px 20px;
  border-radius: 25px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s;
  margin-bottom: 30px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.back-home-btn:hover {
  border-color: #d32f2f;
  color: #d32f2f;
  background: rgba(211, 47, 47, 0.05);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(211, 47, 47, 0.15);
}

.btn-icon {
  font-size: 16px;
  font-weight: bold;
  transition: transform 0.3s;
}

.back-home-btn:hover .btn-icon {
  transform: translateX(-3px);
}

.btn-text {
  letter-spacing: 1px;
}

.title-wrapper {
  text-align: center;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
}

.page-title {
  font-size: 36px;
  color: #333;
  margin-bottom: 10px;
  font-weight: bold;
  letter-spacing: 2px;
}

.page-subtitle {
  color: #999;
  font-size: 14px;
  letter-spacing: 3px;
  text-transform: uppercase;
  margin: 0;
}

.news-list-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
}

.news-item {
  background: #fff;
  padding: 35px 30px;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.08);
  transition: all 0.3s;
  cursor: pointer;
  border: 2px solid transparent;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

.news-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, #d32f2f 0%, #e53935 100%);
  opacity: 0;
  transition: opacity 0.3s;
}

.news-item:hover {
  transform: translateY(-8px);
  box-shadow: 0 15px 35px rgba(211, 47, 47, 0.15);
}

.news-item:hover::before {
  opacity: 1;
}

.news-item.active {
  border-color: #d32f2f;
  box-shadow: 0 10px 30px rgba(211, 47, 47, 0.2);
  background: linear-gradient(135deg, #fff 0%, #ffebee 100%);
}

.news-item.active::before {
  opacity: 1;
}

.news-title {
  font-size: 22px;
  color: #d32f2f;
  margin-bottom: 18px;
  font-weight: bold;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 66px;
}

.news-content-rich {
  color: #666;
  line-height: 1.8;
  margin-bottom: 20px;
  font-size: 14px;
  flex: 1;

  /* 限制显示 3-4 行 */
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 126px;
}

.news-content-rich:hover {
  color: #d32f2f;
}

.read-more-btn {
  color: #d32f2f;
  background: none;
  border: none;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 10px 16px;
  border-radius: 6px;
  margin-bottom: 20px;
  align-self: flex-start;
}

.read-more-btn:hover {
  color: #e53935;
  background-color: rgba(211, 47, 47, 0.08);
  transform: translateX(5px);
}

.read-more-btn::after {
  content: '';
  transition: transform 0.3s;
}

.read-more-btn:hover::after {
  transform: translateX(5px);
}

.news-footer {
  border-top: 2px solid #f0f0f0;
  padding-top: 18px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
  margin-top: auto;
}

.news-date {
  color: #d32f2f;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}

.news-date::before {
  content: '📅';
  font-size: 14px;
}

.news-creator {
  color: #666;
  font-size: 13px;
  font-style: normal;
  display: flex;
  align-items: center;
  gap: 6px;
}

.news-creator::before {
  content: '✍️';
  font-size: 14px;
}

/* 空状态提示 */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  color: #999;
  font-size: 16px;
}

.empty-state::before {
  content: '📰';
  font-size: 64px;
  display: block;
  margin-bottom: 20px;
}

/* 加载中提示 */
.loading-state {
  text-align: center;
  padding: 80px 20px;
  color: #d32f2f;
  font-size: 16px;
  font-weight: 500;
}

.loading-state::before {
  content: '';
  display: inline-block;
  width: 30px;
  height: 30px;
  border: 3px solid #ffebee;
  border-top-color: #d32f2f;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 10px;
  vertical-align: middle;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 页脚 */
.footer {
  background: #333;
  color: #fff;
  padding: 30px 0;
  text-align: center;
}

.footer p {
  margin-bottom: 10px;
  font-size: 14px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .news-list-section {
    padding: 100px 0 60px;
  }

  .list-header {
    margin-bottom: 30px;
  }

  .back-home-btn {
    width: 100%;
    justify-content: center;
    padding: 12px 24px;
  }

  .page-title {
    font-size: 28px;
    letter-spacing: 1px;
  }

  .page-subtitle {
    font-size: 12px;
    letter-spacing: 2px;
  }

  .news-list-container {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .news-item {
    padding: 25px 20px;
  }

  .news-title {
    font-size: 18px;
    min-height: auto;
  }

  .detail-content {
    padding: 30px 20px;
  }

  .detail-title {
    font-size: 24px;
  }

  .detail-meta {
    flex-direction: column;
    gap: 12px;
  }

  .back-btn {
    padding: 10px 20px;
    font-size: 13px;
  }
}
</style>
